import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic } from "lucide-react";

// Iter123k201/k207 · Wake Word "Hei Ginius attiva CoPilot" via Web Speech API.
// Attivo solo per admin con toggle localStorage `copilot_wake_word_enabled === "true"`.
// Si disattiva durante /copilot/{sid} (il mic è già usato da Whisper), tranne che su /copilot/mappa.

const STORAGE_KEY = "copilot_wake_word_enabled";
const LAST_HEARD_KEY = "copilot_wake_last_heard";

// Chrome spesso trascrive male "Ginius" (parola non italiana):
// → genius, junius, jinius, ginnius, genio, ginni, ginny…
// Allarghiamo con una regex fuzzy. Simile per "copilot" → co pilot, co-pilot, pilota…
const ASSISTENTE_RE = /\b(g[eiu]+n+[ij]?[eu]?s?|ginius|genius|junius|jinius|ginnius|geni[oa]?|ginni|ginny|jinni)\b/i;
const COPILOT_RE = /\b(co[\s-]?pil[oa]t[ae]?|pilot[ae]?|copilota)\b/i;
const ACTION_RE = /\b(attiv[aoie]|avvi[ao]|apri|accend[ia]|part[ei]|start|ok|dai)\b/i;

export function isWakeWordEnabled() {
  try { return localStorage.getItem(STORAGE_KEY) === "true"; }
  catch { return false; }
}

export function setWakeWordEnabled(v) {
  try { localStorage.setItem(STORAGE_KEY, v ? "true" : "false"); }
  catch { /* noop */ }
}

export function getLastHeard() {
  try { return localStorage.getItem(LAST_HEARD_KEY) || ""; }
  catch { return ""; }
}

// Match: assistente + copilot OPPURE assistente + action
export function matchesWakePhrase(text) {
  if (!text) return false;
  const low = text.toLowerCase();
  const hasA = ASSISTENTE_RE.test(low);
  const hasC = COPILOT_RE.test(low);
  if (hasA && hasC) return true;
  if (hasA && ACTION_RE.test(low)) return true;
  return false;
}

export function WakeWordListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [listening, setListening] = useState(false);
  const [enabled, setEnabled] = useState(isWakeWordEnabled());
  const recRef = useRef(null);
  const triggeredRef = useRef(0);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setEnabled(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    const iv = setInterval(() => setEnabled(isWakeWordEnabled()), 1500);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(iv); };
  }, []);

  const canRun = () => {
    if (!enabled) return false;
    if (!user || user.role !== "admin") return false;
    if (location.pathname.startsWith("/copilot/") && !location.pathname.endsWith("/mappa")) return false;
    return true;
  };

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    if (!canRun()) {
      if (recRef.current) {
        try { recRef.current.stop(); } catch { /* noop */ }
        recRef.current = null;
      }
      setListening(false);
      return;
    }

    const rec = new SR();
    rec.lang = "it-IT";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 3; // più alternative = più chance di catturare "Ginius"

    rec.onstart = () => setListening(true);
    rec.onend = () => {
      setListening(false);
      // Riavvio automatico (Chrome Android termina spesso dopo silenzio)
      if (canRun() && recRef.current === rec) {
        setTimeout(() => { try { rec.start(); } catch { /* noop */ } }, 400);
      }
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed") {
        toast.error("Permesso microfono negato: ascolto Ginius disattivato");
        setWakeWordEnabled(false);
        setEnabled(false);
      } else if (e.error === "network") {
        try { rec.stop(); } catch { /* noop */ }
      }
      // no-speech / aborted / audio-capture: normali, ignoro
    };
    rec.onresult = (ev) => {
      // Raccolgo tutte le alternative di tutti i result → massimizzo chance di match
      const allTexts = [];
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        for (let j = 0; j < r.length; j++) {
          const t = (r[j].transcript || "").trim();
          if (t) allTexts.push(t);
        }
      }
      if (allTexts.length === 0) return;
      const combined = allTexts.join(" ").toLowerCase().trim();

      // Diagnostica: salvo in storage + dispatch event (la pagina Mappa lo mostra)
      try { localStorage.setItem(LAST_HEARD_KEY, combined.slice(0, 200)); } catch { /* noop */ }
      window.dispatchEvent(new CustomEvent("wake-word-heard", { detail: { text: combined } }));

      // Testo tutte le alternative + la stringa combinata
      const matched = allTexts.some(matchesWakePhrase) || matchesWakePhrase(combined);

      if (matched && Date.now() - triggeredRef.current > 4000) {
        triggeredRef.current = Date.now();
        toast.success("Hei! Attivo il CoPilot…", { duration: 2500 });
        window.dispatchEvent(new CustomEvent("wake-word-triggered", { detail: { text: combined } }));
        try { rec.stop(); } catch { /* noop */ }
        setTimeout(() => navigate("/copilot/mappa?wake=1"), 300);
      }
    };

    recRef.current = rec;
    try { rec.start(); } catch { /* noop */ }

    return () => {
      recRef.current = null;
      try { rec.stop(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user?.role, location.pathname]);

  if (!enabled || !user || user.role !== "admin") return null;

  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) return null;

  return (
    <div data-testid="wake-word-badge"
      className={`fixed bottom-4 right-4 z-30 px-3 py-1.5 rounded-full shadow-lg text-xs font-bold flex items-center gap-1.5 pointer-events-none ${listening ? "bg-emerald-600 text-white" : "bg-slate-500 text-white opacity-70"}`}>
      <Mic className={`w-3.5 h-3.5 ${listening ? "animate-pulse" : ""}`} />
      {listening ? "Ascolto Ginius…" : "Ginius standby"}
    </div>
  );
}

export default WakeWordListener;
